import { useCallback, useEffect } from "react";
import { useAudioContext } from "./AudioContext";

const MUSIC_ENABLED_KEY = "music-enabled";

export function useMusicPlayer(src: string) {
  const { audioRef, isPlayable, isPlaying, cleanup } = useAudioContext();

  const play = useCallback(async () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.src = src;
      await audioRef.current.play();
    } catch (error) {
      console.warn("Playback failed:", error);
    }
  }, [audioRef, src]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, [audioRef]);

  const setMusicEnabled = (enabled: boolean) => {
    localStorage.setItem(MUSIC_ENABLED_KEY, enabled ? "true" : "false");
  };

  const getMusicEnabled = () => {
    return localStorage.getItem(MUSIC_ENABLED_KEY) !== "false";
  };

  const toggle = useCallback(async () => {
    if (isPlaying) {
      pause();
      setMusicEnabled(false);
    } else {
      await play();
      setMusicEnabled(true);
    }
  }, [isPlaying, play, pause]);

  useEffect(() => {
    const wasEnabled = getMusicEnabled();
    if (wasEnabled) {
      play().catch(() => {
        console.log(
          "Auto-play was blocked by browser. Waiting for user interaction."
        );
      });
    }
  }, [play]);

  return {
    isPlaying,
    isPlayable,
    play,
    pause,
    toggle,
    cleanup,
    setMusicEnabled,
    getMusicEnabled,
  };
}
